"use client";

import { useCallback } from "react";
import { ensureCsrfToken } from "@/lib/csrf-client";
import type { WithCsrf } from "@/lib/withSecurity";

/**
 * Wraps a security-protected server action to auto-inject the CSRF token.
 *
 * Without this hook, every call site has to do:
 * ```ts
 * const csrfToken = await ensureCsrfToken();
 * await someAction({ ...payload, csrfToken });
 * ```
 *
 * With this hook:
 * ```ts
 * const someAction = useServerAction(originalAction);
 * await someAction(payload);   // csrfToken auto-injected
 * ```
 *
 * The wrapped function takes the same input as the action MINUS the
 * `csrfToken` field. Actions that take only `{ csrfToken }` (like logout)
 * become callable as `await wrapped()` with no arguments.
 *
 * @example Single argument with payload
 * ```ts
 * "use client";
 * import { useServerAction } from "@/hooks/useServerAction";
 * import { createStaffAction } from "@/actions/serverActions/staff/createStaffAction";
 *
 * function CreateStaffForm() {
 *   const createStaff = useServerAction(createStaffAction);
 *
 *   const onSubmit = async (data: FormData) => {
 *     const result = await createStaff({
 *       first_name: data.get("first_name") as string,
 *       email: data.get("email") as string,
 *     });
 *     // result.success, result.message, result.data
 *   };
 * }
 * ```
 *
 * @example No-argument action (logout, refresh)
 * ```ts
 * const logout = useServerAction(logoutAction);
 * await logout();   // empty payload, csrfToken auto-injected
 * ```
 */
export function useServerAction<TFullInput extends WithCsrf, TResult>(
  action: (input: TFullInput) => Promise<TResult>,
) {
  type ClientInput = Omit<TFullInput, "csrfToken">;

  return useCallback(
    async (input: ClientInput = {} as ClientInput): Promise<TResult> => {
      const csrfToken = await ensureCsrfToken();
      return action({ ...input, csrfToken } as TFullInput);
    },
    [action],
  );
}
